import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import walletService from '../services/walletService';
import { useAuth } from '../context/AuthContext';

// Hook to get wallet query options
export const getWalletQueryOptions = (id: number) => ({
  queryKey: ['wallet', id],
  queryFn: () => walletService.getWallet(id),
  enabled: !!id,
  staleTime: 1000 * 60 * 5, // 5 minutes
});

// Hook to get wallet transactions query options
export const getWalletTransactionsQueryOptions = (walletId: number) => ({
  queryKey: ['wallet-transactions', walletId],
  queryFn: () => walletService.getWalletTransactions(walletId),
  enabled: !!walletId,
  staleTime: 1000 * 60 * 5, // 5 minutes
});

// Custom hook for wallets
export const useWallets = () => {
  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuth();
  
  // Fetch wallets list only when authenticated
  const { 
    data: response, 
    isLoading,
    error,
    isError,
  } = useQuery({
    queryKey: ['wallets'],
    queryFn: () => walletService.getWallets(),
    enabled: isAuthenticated, // Only fetch when authenticated
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
  
  // Extract wallets from the response
  const wallets = response?.data?.data || [];
  
  // Add funds to wallet
  const addFunds = useMutation({
    mutationFn: walletService.addFunds,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wallets'] });
    }
  });
  
  // Withdraw funds from wallet
  const withdrawFunds = useMutation({
    mutationFn: walletService.withdrawFunds,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wallets'] });
    }
  });

  // Refetch wallets
  const refetchWallets = () => {
    queryClient.invalidateQueries({ queryKey: ['wallets'] });
  };
  
  return {
    wallets,
    isLoading,
    error,
    isError,
    refetch: refetchWallets,
    addFunds: addFunds.mutate,
    isPendingAddFunds: addFunds.isPending,
    addFundsError: addFunds.error,
    withdrawFunds: withdrawFunds.mutate,
    isPendingWithdrawFunds: withdrawFunds.isPending,
    withdrawFundsError: withdrawFunds.error
  };
};

// Custom hook for a single wallet
export const useWallet = (id: number) => {
  return useQuery(getWalletQueryOptions(id));
};

// Custom hook for wallet transactions
export const useWalletTransactions = (walletId: number) => {
  return useQuery(getWalletTransactionsQueryOptions(walletId));
};

export default useWallets; 